import React, { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Calendar, Users, MessageCircle, Clock, MapPin, ChevronRight } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function PortalDashboard() {
  const { team, userRole, canManage, token } = useOutletContext()
  const [events, setEvents] = useState([])
  const [roster, setRoster] = useState({ players: [], staff: [] })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (team?.team_id) {
      fetchData()
    }
  }, [team?.team_id])

  const fetchData = async () => {
    try {
      // Fetch upcoming events
      const eventsRes = await fetch(`${API_URL}/api/teams/${team.team_id}/events`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json()
        // Filter to only upcoming events and limit to 5
        const upcoming = (eventsData || [])
          .filter(e => new Date(e.event_date || e.start_time) >= new Date())
          .slice(0, 5)
        setEvents(upcoming)
      }

      // Fetch roster
      const rosterRes = await fetch(`${API_URL}/api/teams/${team.team_id}/roster`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      if (rosterRes.ok) {
        const rosterData = await rosterRes.json()
        // Roster can be a list with type field or an object with players/staff arrays
        if (Array.isArray(rosterData)) {
          const players = rosterData.filter(r => r.type === 'player')
          const staff = rosterData.filter(r => r.type === 'staff')
          setRoster({ players, staff })
        } else {
          setRoster(rosterData)
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  const formatTime = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">{team?.name}</h1>
        <p className="text-zinc-400 mt-1">
          {team?.sport && <span className="capitalize">{team.sport}</span>}
          {team?.season && <span> &middot; {team.season}</span>}
          {userRole && <span> &middot; <span className="capitalize">{userRole.replace('_', ' ')}</span></span>}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{events.length}</p>
              <p className="text-sm text-zinc-400">Upcoming</p>
            </div>
          </div>
        </div>
        
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{roster.players?.length || 0}</p>
              <p className="text-sm text-zinc-400">Players</p>
            </div>
          </div>
        </div>
        
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{roster.staff?.length || 0}</p>
              <p className="text-sm text-zinc-400">Staff</p>
            </div>
          </div>
        </div>
        
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">-</p>
              <p className="text-sm text-zinc-400">Messages</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Upcoming Events */}
        <div className="bg-zinc-800 rounded-xl border border-zinc-700">
          <div className="flex items-center justify-between p-4 border-b border-zinc-700">
            <h2 className="font-semibold text-white">Upcoming Events</h2>
            <a href={`/portal/team/${team?.team_id}/schedule`} className="text-sm text-zinc-400 hover:text-zinc-200 flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </a>
          </div>
          
          <div className="divide-y divide-gray-100">
            {events.length === 0 ? (
              <div className="p-8 text-center text-zinc-400">
                <Calendar className="w-10 h-10 text-zinc-500 mx-auto mb-2" />
                <p>No upcoming events</p>
              </div>
            ) : (
              events.slice(0, 5).map(event => (
                <div key={event.event_id} className="p-4 hover:bg-zinc-900 transition-colors">
                  <div className="flex gap-4">
                    <div className="text-center flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-zinc-800 flex flex-col items-center justify-center">
                        <span className="text-xs text-zinc-400 uppercase">
                          {new Date(event.event_date || event.start_time || event.date).toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                        <span className="text-lg font-bold text-white">
                          {new Date(event.event_date || event.start_time || event.date).getDate()}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{event.title || event.name}</p>
                      <div className="flex items-center gap-3 mt-1 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formatTime(event.event_date || event.start_time || event.date)}
                        </span>
                        {event.location && (
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{event.location}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        event.event_type === 'game' ? 'bg-red-500/20 text-red-400' :
                        event.event_type === 'practice' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-zinc-800 text-zinc-200'
                      }`}>
                        {event.event_type || 'Event'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Team Roster Preview */}
        <div className="bg-zinc-800 rounded-xl border border-zinc-700">
          <div className="flex items-center justify-between p-4 border-b border-zinc-700">
            <h2 className="font-semibold text-white">Team Roster</h2>
            <a href={`/portal/team/${team?.team_id}/roster`} className="text-sm text-zinc-400 hover:text-zinc-200 flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </a>
          </div>
          
          <div className="p-4">
            {/* Staff */}
            {roster.staff?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Staff</p>
                <div className="flex flex-wrap gap-2">
                  {roster.staff.slice(0, 4).map((member, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-zinc-900 rounded-lg px-3 py-2">
                      <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
                        {member.picture ? (
                          <img src={member.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-medium text-zinc-300">
                            {member.name?.charAt(0) || '?'}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{member.name}</p>
                        <p className="text-xs text-zinc-400 capitalize">{member.role?.replace('_', ' ')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Players */}
            {roster.players?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Players</p>
                <div className="flex flex-wrap gap-2">
                  {roster.players.slice(0, 6).map((player, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-zinc-900 rounded-lg px-3 py-2">
                      <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-sm font-bold">
                        {player.jersey_number || player.first_name?.charAt(0) || '?'}
                      </div>
                      <span className="text-sm font-medium text-white">
                        {player.first_name} {player.last_name?.charAt(0)}.
                      </span>
                    </div>
                  ))}
                  {roster.players.length > 6 && (
                    <div className="flex items-center text-sm text-zinc-400 px-3">
                      +{roster.players.length - 6} more
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {roster.players?.length === 0 && roster.staff?.length === 0 && (
              <div className="text-center py-8 text-zinc-400">
                <Users className="w-10 h-10 text-zinc-500 mx-auto mb-2" />
                <p>No roster data</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
